import { useTranslation } from 'react-i18next';
import type {
  DynamicPricingCalendarApartment,
  DynamicPricingCalendarDay,
  DynamicPricingCalendarDayDetailResponse,
} from '@/shared/api';
import { offeringLabel } from './calendarDisplay';
import { DynamicPricingCalculationSection } from './DynamicPricingCalculationSection';
import { DynamicPricingExposureSection } from './DynamicPricingExposureSection';
import { DynamicPricingFact as Fact } from './DynamicPricingFact';

export function DynamicPricingTechnicalDetails({
  apartment,
  day,
  detail,
  detailLoading,
  detailError,
}: {
  apartment: DynamicPricingCalendarApartment;
  day: DynamicPricingCalendarDay;
  detail?: DynamicPricingCalendarDayDetailResponse;
  detailLoading: boolean;
  detailError: boolean;
}) {
  const { t } = useTranslation();
  const publication = day.publication;
  return (
    <details className="border-line border-t pt-4">
      <summary className="min-h-11 cursor-pointer py-3 text-sm font-semibold">
        {t('admin.dynamicPricing.calendar.details.technicalDetails')}
      </summary>
      <DynamicPricingCalculationSection day={day} />
      <DynamicPricingExposureSection
        apartment={apartment}
        detail={detail}
        loading={detailLoading}
        error={detailError}
      />
      <section className="border-line mt-4 space-y-2 border-t pt-4 text-sm">
        <Fact
          label={t('admin.dynamicPricing.calendar.details.offeringStatus')}
          value={offeringLabel(day.offeringState)}
        />
        <Fact
          label={t('admin.dynamicPricing.calendar.details.configurationVersion')}
          value={day.configurationVersionId ?? '—'}
          mono
        />
        <Fact
          label={t('admin.dynamicPricing.calendar.details.strategyProfile')}
          value={day.strategyProfileVersionId ?? '—'}
          mono
        />
        {publication?.lastErrorCode && (
          <Fact
            label={t('admin.dynamicPricing.calendar.details.technicalErrorCode')}
            value={publication.lastErrorCode}
            mono
          />
        )}
      </section>
      {day.warningCodes.length > 0 && (
        <section
          aria-label={t('admin.dynamicPricing.calendar.details.warnings')}
          className="bg-amber/15 mt-4 rounded-xl p-3 text-sm"
        >
          {day.warningCodes.join(' · ')}
        </section>
      )}
    </details>
  );
}
